import React, { useReducer, createContext, Dispatch } from 'react';
import { InvoiceStatus } from './enums/InvoiceStatusEnums';
import {
  IAddress,
  ICompany,
  IContact,
  IInvoice,
  IMembership,
  IPaymentDetails,
  IProduct,
  IUser
} from './generated/graphql';
import { ActionType, UserType } from './models/appContext';
import { ConfirmationType } from './models/confirmation';
import { ContactType } from './models/contact';
import { RecipientListType } from './models/recipientList';
import { MembershipType } from './models/membership';
import { MessageType } from './models/message';
import { mainReducer } from './reducers/mainReducer';
import { getRoutes, IRouteModel } from './Routes';

type Props = {
  children: JSX.Element;
};

type InvoiceErrors = {
  company?: string;
  contact?: string;
  address?: string;
  product?: string;
  isPaymentDetailsInEditMode?: boolean;
};

type InvoiceType = {
  id: string | undefined;
  company: ICompany | null;
  contact: IContact | null;
  address: IAddress | null;
  status: InvoiceStatus;
  errors: InvoiceErrors;
  paymentDetails: IPaymentDetails | undefined;
  isPaymentDetailsInEditMode: boolean;
  productInfo: InvoiceProductInfo | null;
  invoice_full: IInvoice | null;
  notes: string | null;
  membership: IMembership | null;
  po_nr: string | null;
  remarks: string | null;
};

export type InvoiceProductInfo = {
  product: IProduct | null;
  discountPercent: number | null;
  discount: number;
  vatPercent: number | null;
  vat: number;
  total: number;
};

type CompanyType = {
  name: string | null;
  address_name: string | null | undefined;
};

type InitialStateType = {
  selectedCompany: CompanyType;
  message: MessageType;
  confirmation: ConfirmationType;
  user: UserType;
  selectedMembership: MembershipType | null;
  selectedContact: ContactType;
  selectedInvoice: InvoiceType | null;
  routes: IRouteModel[];
  selectedUser: IUser | null;
  selectedProduct: string | null;
  selectedRecipientList: RecipientListType | null;
};

export const InitialState = {
  user: { data: null, token: null },
  message: { open: false, text: null, severity: null, autoHide: 3000 },
  confirmation: { open: false },
  selectedCompany: { name: null, address_name: null },
  selectedContact: { name: null, company_name: null },
  routes: getRoutes(),
  selectedProduct: null,
  selectedMembership: null,
  selectedInvoice: null,
  selectedRecipientList: null,
  selectedUser: null
};

const AppContext = createContext<{
  state: InitialStateType;
  dispatch: Dispatch<ActionType>;
}>({
  state: InitialState,
  dispatch: () => null
});

const AppProvider: React.FunctionComponent<Props> = ({ children }) => {
  const [state, dispatch] = useReducer(mainReducer, InitialState);

  return <AppContext.Provider value={{ state, dispatch }}>{children}</AppContext.Provider>;
};

export { AppProvider, AppContext, InvoiceProductInfo, InitialStateType };
